import React from 'react';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSpreadsheet } from '@fortawesome/pro-solid-svg-icons/faFileSpreadsheet';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import classNames from 'classnames';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { Button } from 'src/components/design-system';
import { formatFileSize } from '../helpers/upload.utils';
import { TabResultCard } from './tab-result-card';
import type { ImportPlan } from '../helpers/analysis';
import styles from './review-step.module.scss';

const EM_DASH = '—';

const formatRangeDate = (date: string | null) => {
  if (!date) return EM_DASH;
  const parsed = DateTime.fromISO(date);
  return parsed.isValid ? parsed.toFormat('LLL d, yyyy') : EM_DASH;
};

type ReviewStepProps = {
  fileName: string;
  fileSize: number;
  plan: ImportPlan;
  removedCount: number | null;
  onBack: () => void;
  onUpload: () => void;
};

export const ReviewStep = ({
  fileName,
  fileSize,
  plan,
  removedCount,
  onBack,
  onUpload,
}: ReviewStepProps) => {
  const lang = useSelector(getLang('SETTINGS'));

  const shiftCount = (count: number) => lang.SHIFTS_COUNT.replace('{count}', formatNumber(count));

  const stats = [
    { label: lang.SHIFTS_ADDED, value: shiftCount(plan.events.length), tone: styles.added },
    {
      label: lang.SHIFTS_REMOVED,
      value: removedCount === null ? EM_DASH : shiftCount(removedCount),
      tone: styles.removed,
    },
    {
      label: lang.SHIFTS_TOTAL_EDITS,
      value: removedCount === null ? EM_DASH : shiftCount(plan.events.length + removedCount),
    },
    { label: lang.SHIFTS_START_DATE, value: formatRangeDate(plan.startDate) },
    { label: lang.SHIFTS_END_DATE, value: formatRangeDate(plan.endDate) },
  ];

  return (
    <div>
      <div className={styles.summaryCard}>
        <div className={styles.summaryHeader}>
          <span className={styles.fileIcon}>
            <FontAwesomeIcon icon={faFileSpreadsheet} />
          </span>
          <div>
            <span className={styles.fileLabel}>{lang.SHIFTS_IMPORT_FILE}</span>
            <span className={styles.fileName}>
              {fileName}
              <span className={styles.fileSize}> · {formatFileSize(fileSize)}</span>
            </span>
          </div>
        </div>

        <div className={styles.stats}>
          {stats.map(stat => (
            <div key={stat.label} className={styles.stat}>
              <span className={styles.statLabel}>{stat.label}</span>
              <span className={classNames(styles.statValue, stat.tone)}>{stat.value}</span>
            </div>
          ))}
        </div>

        <span className={styles.replaceNotice}>{lang.SHIFTS_REPLACE_NOTICE}</span>
        {removedCount === null && (
          <span className={styles.unverifiedNotice}>{lang.SHIFTS_REMOVED_UNVERIFIED}</span>
        )}
      </div>

      <div className={styles.tabCards}>
        {plan.tabs.map(tab => (
          <TabResultCard key={tab.sheetName} tab={tab} />
        ))}
      </div>

      <div className={styles.footer}>
        <Button type="default" onClick={onBack}>
          <FontAwesomeIcon icon={faChevronLeft} className={styles.backIcon} />
          {lang.SHIFTS_BACK}
        </Button>
        <Button type="primary" disabled={plan.events.length === 0} onClick={onUpload}>
          {lang.SHIFTS_UPLOAD_ACTION}
        </Button>
      </div>
    </div>
  );
};
